import { h } from '@/lib/dom';
import { Card } from '@/components/Card';
import { SkeletonList } from '@/components/Skeleton';
import { Button } from '@/components/Button';
import { syncBadgeFooter } from '@/components/SyncBadge';
import { toast } from '@/components/Toast';
import { data } from '@/lib/data';
import { formatDate, formatLabValue, formatNumber, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import type { CallDetail, HomeBundle, MeasurementMetricSummary, PriorityMarkerItem, TopOutOfRangeItem } from '@/api/types';
import { markdownSpan } from '@/lib/markdown-mini';
import { sectionLabel, sectionEmoji, SECTION_ORDER } from '@/lib/med-section';
import type { MedSection } from '@/lib/med-section';
import { sportLabel, sportEmoji } from '@/lib/sport';
import { strainLabel } from '@/lib/strain';
import { pageLayout } from './layout';

export async function renderHome(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';

  const root = pageLayout({
    user,
    active: 'home',
    children: [
      h('div', { id: 'home-grid', class: 'grid grid-12' }, [
        loadingCard(),
        loadingCard(),
        loadingCard(),
        loadingCard(),
      ]),
      isOwner
        ? h('button', {
            class: 'fab owner-visible',
            type: 'button',
            aria: { label: 'Добавить' },
            on: {
              click: () => {
                navigate('/labs/upload');
              },
            },
          }, '+')
        : null,
    ],
  });

  void (async () => {
    try {
      const bundle = await data.getHome();
      const grid = root.querySelector('#home-grid') as HTMLElement;
      grid.innerHTML = '';
      grid.append(...homeGrid(bundle, isOwner));
    } catch (err) {
      const grid = root.querySelector('#home-grid') as HTMLElement;
      grid.innerHTML = '';
      grid.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, 'Не удалось загрузить данные.'),
        Button({ label: 'Повторить', onClick: () => navigate('/', { replace: true }) }),
      ]));
      console.error(err);
    }
  })();

  return root;
}

function loadingCard(): HTMLElement {
  const card = Card({ title: '', children: [SkeletonList(3)] });
  card.classList.add('col-span-4');
  return card;
}

/** Unified card header: h3 + optional info-tip + right-aligned subtitle. */
function homeCardHeader(opts: {
  title: string;
  subtitle?: string;
  infoTip?: { ariaLabel: string; text: string; duration?: number } | null;
  asLink?: { href: string; ariaLabel: string };
}): HTMLElement {
  const tipBtn = opts.infoTip
    ? h('button', {
        class: 'info-tip',
        type: 'button',
        aria: { label: opts.infoTip.ariaLabel },
        on: {
          click: () => toast(opts.infoTip!.text, { type: 'info', duration: opts.infoTip!.duration ?? 5000 }),
        },
      }, 'ⓘ')
    : null;

  const titleGroup = h('div', { class: 'home-card-title-group' }, [
    h('h3', { class: 'home-card-title' }, opts.title),
    tipBtn,
  ]);

  const subtitleEl = opts.subtitle
    ? h('span', { class: 'home-card-subtitle' }, opts.subtitle)
    : null;

  if (opts.asLink) {
    return h('a', {
      href: opts.asLink.href,
      class: 'home-card-header home-card-header-link',
      'aria-label': opts.asLink.ariaLabel,
    }, [titleGroup, subtitleEl]);
  }

  return h('div', { class: 'home-card-header' }, [titleGroup, subtitleEl]);
}

function outOfRangeRow(item: TopOutOfRangeItem): HTMLElement {
  const isHigh = item.flag === 'H' || item.flag === 'HH';
  const dotColor = isHigh ? 'var(--status-high-fg)' : 'var(--status-low-fg)';

  return h('a', {
    href: `/labs/${item.canonical_key}`,
    class: 'out-of-range-row',
    aria: { label: `${item.display_ru}: ${item.value ?? '—'} ${item.unit}, ${isHigh ? 'выше нормы' : 'ниже нормы'}` },
  }, [
    h('span', { class: 'oor-dot', style: `background:${dotColor};` }),
    h('span', { class: 'oor-name' }, item.display_ru),
    h('span', { class: 'oor-value' }, [
      h('span', { class: 'oor-num' }, item.value != null ? formatLabValue(item.value) : '—'),
      ' ',
      h('span', { class: 'oor-flag', style: `color:${dotColor};` }, item.flag ?? ''),
    ]),
    h('span', { class: 'oor-unit text-secondary' }, item.unit),
    h('span'),
    h('span', { class: 'oor-arrow text-tertiary' }, '→'),
  ]);
}

/**
 * Returns whether a value is within 20% of the nearest reference boundary.
 * (i.e. has traveled >80% of the way from mid-range to the nearest bound.)
 */
function isNearBoundary(value: number, refLow: number | null, refHigh: number | null): boolean {
  if (refLow != null && refHigh != null) {
    const range = refHigh - refLow;
    if (range <= 0) return false;
    const distLow = value - refLow;
    const distHigh = refHigh - value;
    const minDist = Math.min(distLow, distHigh);
    return minDist / range < 0.2;
  }
  if (refHigh != null) {
    return value / refHigh > 0.8;
  }
  if (refLow != null) {
    return refLow / value > 0.8;
  }
  return false;
}

function priorityRow(item: PriorityMarkerItem): HTMLElement {
  const refText = item.ref_low != null && item.ref_high != null
    ? `норма: ${formatNumber(item.ref_low, 1)}–${formatNumber(item.ref_high, 1)} ${item.unit}`
    : item.ref_high != null
      ? `норма: <${formatNumber(item.ref_high, 1)} ${item.unit}`
      : item.ref_low != null
        ? `норма: >${formatNumber(item.ref_low, 1)} ${item.unit}`
        : null;

  let deltaEl: HTMLElement;
  if (item.delta === null || item.delta === undefined) {
    deltaEl = h('span', { class: 'prio-delta text-tertiary' }, '—');
  } else {
    const warn = item.value != null
      ? isNearBoundary(item.value, item.ref_low, item.ref_high)
      : false;
    const deltaColor = warn ? 'var(--status-warn-fg)' : 'var(--text-secondary)';
    const sign = item.delta >= 0 ? '+' : '';
    deltaEl = h('span', { class: 'prio-delta', style: `color: ${deltaColor};` },
      `${sign}${formatNumber(item.delta, 2)} vs пред.`);
  }

  return h('a', {
    href: `/labs/${item.canonical_key}`,
    class: 'priority-row',
    aria: { label: `${item.display_ru}: ${item.value ?? '—'} ${item.unit}, в норме` },
  }, [
    h('span', { class: 'prio-pin', 'aria-hidden': 'true' }, '📌'),
    h('span', { class: 'prio-name' }, item.display_ru),
    h('span', { class: 'prio-value' }, item.value != null ? formatLabValue(item.value) : '—'),
    h('span', { class: 'prio-unit text-secondary' }, item.unit),
    refText ? h('span', { class: 'prio-ref text-tertiary' }, refText) : h('span', { class: 'prio-ref' }),
    deltaEl,
    h('span', { class: 'prio-arrow text-tertiary' }, '→'),
  ]);
}

function prioritySection(items: PriorityMarkerItem[], isOwner: boolean): HTMLElement | null {
  const PANEL_ORDER: Record<string, number> = { lipid: 0, cmp: 1 };
  const sorted = items.slice().sort((a, b) => {
    const pa = PANEL_ORDER[a.panel] ?? 99;
    const pb = PANEL_ORDER[b.panel] ?? 99;
    if (pa !== pb) return pa - pb;
    return a.canonical_key.localeCompare(b.canonical_key);
  });

  const heading = h('div', { class: 'prio-section-heading' }, 'Приоритетные и липидный профиль');

  let listEl: HTMLElement;
  if (items.length === 0) {
    const emptyText = isOwner
      ? 'Ничего не закреплено. Откройте карточку маркёра и нажмите 📌, чтобы закрепить.'
      : 'Приоритетные маркёры не отмечены.';
    listEl = h('p', { class: 'prio-empty text-secondary' }, emptyText);
  } else {
    // Show all in-range priority + lipid-panel markers from the submission (no cap).
    const rows = sorted.map((item) => priorityRow(item));
    listEl = h('div', { class: 'prio-list' }, rows);
  }

  const sep = h('div', { class: 'prio-separator', 'aria-hidden': 'true' });

  return h('div', { class: 'priority-section' }, [sep, heading, listEl]);
}

function latestLabBlock(bundle: HomeBundle, isOwner: boolean): HTMLElement {
  if (!bundle.last_lab) {
    const card = Card({
      title: '',
      children: [
        homeCardHeader({ title: '🧪 Последний анализ' }),
        h('p', null, 'Анализов ещё нет.'),
        isOwner
          ? h('a', { href: '/labs/upload', class: 'btn btn-primary btn-sm mt-2' }, 'Загрузить первый анализ')
          : h('p', { class: 'text-tertiary mt-2' }, 'Владелец загрузит их здесь.'),
      ],
    });
    card.classList.add('col-span-12');
    return card;
  }

  const lab = bundle.last_lab;
  const items = bundle.top_out_of_range;
  const priorityItems = bundle.priority_markers ?? [];
  const fileCount = bundle.submission_file_count ?? 0;

  let oorChildren: Array<HTMLElement | null>;

  if (!items.length) {
    oorChildren = [
      h('div', { class: 'oor-all-normal' }, [
        h('span', { class: 'oor-norm-check', style: 'color: var(--status-norm-fg);' }, '✓'),
        h('span', null, ' В последней сдаче все показатели в референсе'),
      ]),
    ];
  } else {
    // Show ALL out-of-range across the whole submission (no truncation).
    oorChildren = [
      h('div', { class: 'oor-list', role: 'list', aria: { label: 'Маркёры вне нормы' } },
        items.map((item) => outOfRangeRow(item))),
    ];
  }

  const prioSection = prioritySection(priorityItems, isOwner);

  // Subtitle: draw date + file count (a submission is often several files).
  const subtitle = fileCount > 1
    ? `${formatDate(lab.drawn_at, 'long')} · ${fileCount} ${plural(fileCount, ['файл', 'файла', 'файлов'])}`
    : formatDate(lab.drawn_at, 'long');

  const footer = h('a', {
    href: `/labs/submission/${lab.drawn_at}`,
    class: 'btn btn-secondary btn-sm btn-submission-link',
  }, 'Вся сдача →');

  const card = Card({
    title: '',
    children: [
      homeCardHeader({ title: '🧪 Последний анализ', subtitle }),
      ...oorChildren,
      prioSection,
    ],
    footer,
  });
  card.classList.add('col-span-12');
  return card;
}

// Format sleep phase minutes (always minutes — for Deep/REM/Awake).
function fmtMin(min: number | null | undefined): string {
  if (min == null) return '—';
  return `${Math.round(min)}м`;
}

// Format Light sleep phase: hours if ≥ 60m, minutes otherwise.
function fmtLight(min: number | null | undefined): string {
  if (min == null) return '—';
  if (min >= 60) return `${(min / 60).toFixed(1)}ч`;
  return `${Math.round(min)}м`;
}

function ouraCardHome(bundle: HomeBundle): HTMLElement {
  const ouraP = bundle.oura.payload;
  const hasData = ouraP.token_configured !== false && ouraP.avg_sleep_hours != null;

  let children: Array<HTMLElement | null>;

  if (!hasData) {
    children = [
      h('p', { class: 'text-tertiary' }, 'Нет данных.'),
      h('a', { href: '/sleep', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  } else {
    const sleepHours = ouraP.avg_sleep_hours != null
      ? `Средний сон ${ouraP.avg_sleep_hours.toFixed(1)} ч`
      : 'Средний сон — ч';

    const deepStr = ouraP.avg_deep_minutes != null
      ? `Deep ${fmtMin(ouraP.avg_deep_minutes)}`
        + (ouraP.avg_deep_pct != null ? ` (${Math.round(ouraP.avg_deep_pct)}%)` : '')
      : null;
    const remStr = ouraP.avg_rem_minutes != null
      ? `REM ${fmtMin(ouraP.avg_rem_minutes)}`
        + (ouraP.avg_rem_pct != null ? ` (${Math.round(ouraP.avg_rem_pct)}%)` : '')
      : null;
    const lightStr = ouraP.avg_light_minutes != null
      ? `Light ${fmtLight(ouraP.avg_light_minutes)}`
      : null;

    const phaseParts = [deepStr, remStr, lightStr].filter(Boolean);
    const phasesLine = phaseParts.length > 0 ? phaseParts.join(' · ') : null;

    const phasesEl = phasesLine
      ? h('div', { class: 'home-oura-phases' }, [
          phasesLine,
          h('button', {
            class: 'info-tip',
            type: 'button',
            aria: { label: 'Как считаются проценты фаз?' },
            on: {
              click: () => toast(
                'Проценты считаются от общего времени сна, а не от времени в кровати. Например, Deep 19% — это 19% от часов сна, без учёта времени засыпания и пробуждений.',
                { type: 'info', duration: 5000 },
              ),
            },
          }, 'ⓘ'),
        ])
      : null;

    const scoreVal = ouraP.avg_sleep_score != null ? Math.round(ouraP.avg_sleep_score) : '—';
    const scoreEl = h('div', { class: 'home-oura-score' }, [
      `Sleep Score ${scoreVal}`,
      h('button', {
        class: 'info-tip',
        type: 'button',
        aria: { label: 'Как считается Sleep Score?' },
        on: {
          click: () => toast(
            'Sleep Score 0–100 — оценка качества сна от OURA. Учитывает длительность, фазы, эффективность и стабильность режима. Здесь — среднее за 30 дней.',
            { type: 'info', duration: 5000 },
          ),
        },
      }, 'ⓘ'),
    ]);

    const hrvEl = ouraP.avg_hrv != null
      ? h('div', { class: 'home-oura-hrv' }, [
          `HRV ${Math.round(ouraP.avg_hrv)} мс`,
          h('button', {
            class: 'info-tip',
            type: 'button',
            aria: { label: 'Что такое HRV?' },
            on: {
              click: () => toast(
                'HRV — вариабельность пульса во сне, в миллисекундах. Показывает, как быстро восстанавливается нервная система. Чем выше — тем лучше восстановление. Здесь — среднее за 30 дней.',
                { type: 'info', duration: 5000 },
              ),
            },
          }, 'ⓘ'),
        ])
      : null;

    children = [
      h('div', null, [
        h('div', { class: 'home-oura-main' }, sleepHours),
        phasesEl,
        scoreEl,
        hrvEl,
      ]),
      h('a', { href: '/sleep', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  }

  const card = Card({
    title: '',
    children: [
      homeCardHeader({ title: '😴 Сон', subtitle: '30 дней' }),
      ...children,
    ],
    footer: syncBadgeFooter(bundle.oura),
  });
  card.classList.add('col-span-4');
  return card;
}

function nutritionCardHome(bundle: HomeBundle): HTMLElement {
  const nutrP = bundle.nutrition.payload;
  const hasData = nutrP.avg_kcal != null || nutrP.days_with_log != null;

  const waterP = bundle.hydration.payload;
  const waterEl = waterP.avg_liters != null
    ? h('div', { class: 'home-nutr-water' },
        `💧 Вода: ${waterP.avg_liters.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} л/день`
        + (waterP.days_with_log != null ? ` · ${waterP.days_with_log} / 30` : ''))
    : null;

  let children: Array<HTMLElement | null>;

  if (!hasData) {
    children = [
      waterEl
        ? h('div', null, [h('p', { class: 'text-tertiary' }, 'Нет данных о питании.'), waterEl])
        : h('p', { class: 'text-tertiary' }, 'Нет данных.'),
      h('a', { href: '/nutrition', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  } else {
    const kcal = nutrP.avg_kcal != null ? `${Math.round(nutrP.avg_kcal).toLocaleString('ru-RU')} ккал/день` : '— ккал/день';
    const protein = nutrP.avg_protein_g != null ? `${Math.round(nutrP.avg_protein_g)}` : '—';
    const fat = nutrP.avg_fat_g != null ? `${Math.round(nutrP.avg_fat_g)}` : '—';
    const fatSat = nutrP.avg_fat_saturated_g != null ? `${Math.round(nutrP.avg_fat_saturated_g)}` : null;
    const carbs = nutrP.avg_carbs_g != null ? `${Math.round(nutrP.avg_carbs_g)}` : '—';

    const logEl = nutrP.days_with_log != null
      ? h('div', { class: 'home-nutr-log' }, [
          `Дни с логом: ${nutrP.days_with_log} / 30`,
          h('button', {
            class: 'info-tip',
            type: 'button',
            aria: { label: 'Что такое «дни с логом»?' },
            on: {
              click: () => toast(
                'Дни, в которые был зафиксирован хотя бы один приём пищи. Знаменатель 30 — фиксированное окно, даже если не все дни прошли.'
                + (nutrP.days_skipped ? ` Пропущенные (очищенные вручную) дни — ${nutrP.days_skipped} — не входят в средние.` : ''),
                { type: 'info', duration: 4000 },
              ),
            },
          }, 'ⓘ'),
        ])
      : null;

    children = [
      h('div', null, [
        h('div', { class: 'home-nutr-kcal' }, kcal),
        h('div', { class: 'home-nutr-macros' },
          `Б ${protein} · Ж ${fat}${fatSat ? ` (${fatSat} нас.)` : ''} · У ${carbs}`),
        waterEl,
        logEl,
      ]),
      h('a', { href: '/nutrition', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  }

  const card = Card({
    title: '',
    children: [
      homeCardHeader({ title: '🍽 Питание', subtitle: '30 дней' }),
      ...children,
    ],
    footer: syncBadgeFooter(bundle.nutrition),
  });
  card.classList.add('col-span-4');
  return card;
}

function whoopCardHome(bundle: HomeBundle): HTMLElement {
  const whoopP = bundle.whoop.payload;
  const hasData = whoopP.connected !== false && whoopP.total_workouts != null;

  let children: Array<HTMLElement | null>;

  if (!hasData) {
    children = [
      h('p', { class: 'text-tertiary' }, 'Нет данных за последние 30 дней.'),
      h('a', { href: '/training', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  } else {
    const groups = new Map<string, { emoji: string | null; count: number; totalMin: number }>();
    for (const s of whoopP.by_sport ?? []) {
      const label = sportLabel(s.sport_name);
      const emoji = sportEmoji(s.sport_name);
      const existing = groups.get(label);
      if (existing) {
        existing.count += s.count;
        existing.totalMin += s.total_min ?? 0;
      } else {
        groups.set(label, { emoji, count: s.count, totalMin: s.total_min ?? 0 });
      }
    }
    const sortedGroups = [...groups.entries()].sort((a, b) => b[1].count - a[1].count);
    const sportParts = sortedGroups.map(([label, g]) =>
      g.emoji ? `${g.emoji} ${label} × ${g.count}` : `${label} × ${g.count}`
    );

    const durationStr = whoopP.total_duration_hours != null
      ? `${whoopP.total_duration_hours.toFixed(1)} ч`
      : '—';
    const row1 = h('div', { class: 'home-whoop-total' },
      `${whoopP.total_workouts} тренировок · ${durationStr}`);

    const row2 = sportParts.length > 0
      ? h('div', { class: 'home-whoop-sports' }, sportParts.join(' · '))
      : null;

    const strainInfo = strainLabel(whoopP.avg_strain ?? null);
    const strainValueStr = whoopP.avg_strain != null ? whoopP.avg_strain.toFixed(1) : '—';
    const strainLabelStr = strainInfo ? strainInfo.label : '—';
    const row3 = h('div', { class: 'home-whoop-strain' }, [
      h('span', { class: 'strain-term' }, 'Strain'),
      ` в тренировочный день ${strainValueStr} · ${strainLabelStr}`,
      h('button', {
        class: 'info-tip',
        type: 'button',
        aria: { label: 'Что такое Strain?' },
        on: {
          click: () => toast(
            'Strain — нагрузка дня по шкале 0–21. До 10 — лёгкий день, 10–14 — умеренный, 14–18 — тяжёлый, 18+ — пиковый. Здесь показано среднее только по дням с тренировками.',
            { type: 'info', duration: 5000 },
          ),
        },
      }, 'ⓘ'),
    ]);

    const row4 = whoopP.active_days != null
      ? h('div', { class: 'home-whoop-active' }, [
          `Активных дней: ${whoopP.active_days} / 30`,
          h('button', {
            class: 'info-tip',
            type: 'button',
            aria: { label: 'Что считается активным днём?' },
            on: {
              click: () => toast(
                'Активный день — день с заметной физической нагрузкой. WHOOP считает по суточному циклу нагрузки, а не только по записанным тренировкам. Порог умеренной нагрузки — примерно половина нормы активного дня.',
                { type: 'info', duration: 5000 },
              ),
            },
          }, 'ⓘ'),
        ])
      : null;

    children = [
      h('div', null, [row1, row2, row3, row4].filter(Boolean) as HTMLElement[]),
      h('a', { href: '/training', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  }

  const card = Card({
    title: '',
    children: [
      homeCardHeader({ title: '🏋 Тренировки', subtitle: '30 дней' }),
      ...children,
    ],
    footer: syncBadgeFooter(bundle.whoop),
  });
  card.classList.add('col-span-4');
  return card;
}

function medsCardHome(bundle: HomeBundle, isOwner: boolean): HTMLElement {
  const meds = bundle.active_meds;
  let children: Array<HTMLElement | string | null>;

  if (!meds.length) {
    children = [
      h('p', null, 'Препаратов не задано.'),
      isOwner ? h('a', { href: '/meds', class: 'btn btn-secondary btn-sm mt-2' }, 'Добавить') : null,
    ];
  } else if (meds.length > 4) {
    const grouped = new Map<MedSection, typeof meds>();
    for (const m of meds) {
      const sec = (m.section as MedSection) in { early_morning: 1, workout: 1, breakfast: 1, lunch: 1, dinner: 1, evening: 1, bedtime: 1, other: 1 }
        ? (m.section as MedSection)
        : 'other';
      if (!grouped.has(sec)) grouped.set(sec, []);
      grouped.get(sec)!.push(m);
    }
    const rows = SECTION_ORDER
      .filter((s) => grouped.has(s))
      .map((s) => {
        const list = grouped.get(s)!;
        const emoji = sectionEmoji(s);
        const label = sectionLabel(s);
        const names = list.map((m) => `${m.name} ${m.dose}`).join(', ');
        return h('div', { class: 'meds-home-section-row' }, [
          h('span', { class: 'meds-home-section-label' }, emoji ? `${emoji} ${label}` : label),
          h('span', { class: 'meds-home-section-names text-secondary' }, names),
        ]);
      });
    children = [h('div', { class: 'meds-home-compact' }, rows)];
  } else {
    const grouped = new Map<MedSection, typeof meds>();
    for (const m of meds) {
      const sec = (m.section as MedSection) in { early_morning: 1, workout: 1, breakfast: 1, lunch: 1, dinner: 1, evening: 1, bedtime: 1, other: 1 }
        ? (m.section as MedSection)
        : 'other';
      if (!grouped.has(sec)) grouped.set(sec, []);
      grouped.get(sec)!.push(m);
    }
    const sections = SECTION_ORDER.filter((s) => grouped.has(s));
    const rows: HTMLElement[] = [];
    for (const s of sections) {
      const list = grouped.get(s)!;
      const emoji = sectionEmoji(s);
      const label = sectionLabel(s);
      rows.push(h('div', { class: 'meds-home-group-header' }, emoji ? `${emoji} ${label}` : label));
      for (const m of list) {
        rows.push(h('div', { class: 'home-meds-item' }, [
          h('span', { class: 'home-meds-item-name' }, m.name),
          h('span', { class: 'home-meds-item-dose' }, ` ${m.dose}`),
        ]));
      }
    }
    children = [h('div', null, rows)];
  }

  const card = Card({
    title: '',
    children: [
      homeCardHeader({ title: '💊 Активные препараты', subtitle: String(meds.length) }),
      ...children,
    ],
    footer: h('a', { href: '/meds', class: 'btn btn-ghost btn-sm' }, 'Все препараты →'),
  });
  card.classList.add('col-span-12');
  return card;
}

function nextCallCard(nextCall: CallDetail | null, isOwner: boolean): HTMLElement {
  // Format date compact: YYYY-MM-DD → DD.MM.YYYY
  function fmtCompact(iso: string): string {
    const [y, m, d] = iso.split('-') as [string, string, string];
    return `${d}.${m}.${y}`;
  }

  function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  // Determine overdue state
  const overdue = nextCall !== null && !nextCall.is_archived && nextCall.date < todayIso();
  const overdueDays = overdue && nextCall
    ? Math.round((new Date(todayIso()).getTime() - new Date(nextCall.date).getTime()) / 86400000)
    : 0;

  // Build subtitle elements: date + optional overdue badge
  const subtitleChildren: (HTMLElement | null)[] = [];
  if (nextCall) {
    subtitleChildren.push(h('span', { class: 'home-card-subtitle' }, fmtCompact(nextCall.date)));
    if (overdue) {
      subtitleChildren.push(h('span', { class: 'home-agenda-overdue-badge' }, [
        h('span', { 'aria-hidden': 'true' }, '⚠'),
        ` Просрочен ${overdueDays} дн.`,
      ]));
    }
  }

  // Build header manually to support overdue badge alongside subtitle
  const titleGroup = h('div', { class: 'home-card-title-group' }, [
    h('h3', { class: 'home-card-title' }, '📋 К обсуждению'),
  ]);
  const headerEl = h('div', {
    class: 'home-card-header',
  }, [titleGroup, ...subtitleChildren.filter(Boolean) as HTMLElement[]]);

  let children: Array<HTMLElement | string | null>;

  if (!nextCall) {
    // Empty state
    children = [
      h('p', null, 'Звонков не запланировано'),
      isOwner
        ? h('a', { href: '/agenda', class: 'btn btn-ghost btn-sm' }, '+ Запланировать созвон →')
        : null,
    ];
  } else if (nextCall.items.length === 0) {
    // Has call but no items
    children = [
      h('p', { class: 'text-tertiary' }, isOwner ? 'Тем пока нет.' : 'Тем к звонку нет.'),
      h('a', { href: `/agenda/${nextCall.id}`, class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Открыть звонок →'),
    ];
  } else {
    const rows = nextCall.items.map((item) => {
      const cbEl = h('span', {
        class: `home-agenda-checkbox${item.is_done ? ' home-agenda-checkbox--checked' : ''}`,
        role: isOwner ? 'checkbox' : 'img',
        'aria-checked': isOwner ? String(item.is_done) : undefined,
        'aria-label': item.is_done ? 'Выполнено' : 'Не выполнено',
        tabindex: isOwner ? '0' : undefined,
      }, item.is_done ? '☑' : '☐');
      const row = h('div', {
        class: `home-agenda-item${item.is_done ? ' home-agenda-item--done' : ''}`,
      }, [cbEl, h('span', { class: 'home-agenda-body' })]);
      const bodyEl = row.querySelector('.home-agenda-body') as HTMLElement;
      bodyEl.append(...Array.from(markdownSpan(item.body).childNodes));
      if (isOwner) {
        const toggle = async (): Promise<void> => {
          const next = !item.is_done;
          try {
            await data.updateAgendaItem(item.id, { is_done: next });
            navigate('/', { replace: true });
          } catch {
            toast('Не удалось обновить', { type: 'error' });
          }
        };
        cbEl.addEventListener('click', () => void toggle());
        cbEl.addEventListener('keydown', (ev: KeyboardEvent) => {
          if (ev.key === ' ' || ev.key === 'Enter') { ev.preventDefault(); void toggle(); }
        });
      }
      return row;
    });

    children = [
      h('div', { class: 'home-agenda-list' }, rows),
      h('a', { href: `/agenda/${nextCall.id}`, class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Открыть звонок →'),
    ];
  }

  const card = Card({
    title: '',
    children: [headerEl, ...children.filter(Boolean) as HTMLElement[]],
  });
  card.classList.add('col-span-12');
  return card;
}

function measurementTile(metric: MeasurementMetricSummary): HTMLElement {
  const valueStr = metric.value != null ? formatNumber(metric.value, 1) : '—';
  const showUnit = metric.value != null;

  let deltaStr: string;
  let arrow: string;
  if (metric.delta_prev == null) {
    arrow = '—';
    deltaStr = '';
  } else if (metric.delta_prev === 0) {
    arrow = '—';
    deltaStr = '';
  } else if (metric.delta_prev > 0) {
    arrow = '↑';
    deltaStr = ` +${formatNumber(metric.delta_prev, 1)}`;
  } else {
    arrow = '↓';
    deltaStr = ` ${formatNumber(metric.delta_prev, 1)}`;
  }

  let contextEl: HTMLElement | null = null;
  if (metric.delta_prev != null && metric.delta_prev_date) {
    const parts = metric.delta_prev_date.split('-');
    if (parts.length === 3) {
      const day = parts[2]!.replace(/^0/, '');
      const month = parts[1];
      contextEl = h('div', { class: 'mt-context' }, `vs ${day}.${month}`);
    }
  }

  const deltaEl = metric.delta_prev == null
    ? h('div', { class: 'mt-delta' }, '—')
    : h('div', { class: 'mt-delta' }, `${arrow}${deltaStr}`);

  return h('div', { class: 'measurement-tile' }, [
    h('div', { class: 'mt-label' }, metric.label),
    h('div', { class: 'mt-value' }, showUnit
      ? [valueStr, ' ', h('span', { class: 'mt-unit' }, metric.unit)]
      : ['—']),
    deltaEl,
    contextEl,
  ].filter(Boolean) as Array<HTMLElement | string>);
}

function measurementsCardHome(bundle: HomeBundle): HTMLElement {
  const summary = bundle.measurements;
  const payload = summary.payload;
  const hasData = !payload.error && payload.latest_date != null;

  let children: Array<HTMLElement | null>;

  if (!hasData) {
    children = [
      h('p', { class: 'text-tertiary' },
        'Нет данных. Иван ведёт обмеры в Notion — данные подтянутся автоматически.'),
      h('a', { href: '/measurements', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  } else {
    const tilesEl = h('div', { class: 'measurement-tiles' },
      payload.metrics.map((m) => measurementTile(m)));

    children = [
      tilesEl,
      h('a', { href: '/measurements', class: 'btn btn-ghost btn-sm home-card-more-link' }, 'Подробнее →'),
    ];
  }

  const card = Card({
    title: '',
    children: [
      homeCardHeader({
        title: '📏 Измерения',
        subtitle: hasData ? formatDate(payload.latest_date, 'long') : undefined,
      }),
      ...children,
    ],
    footer: syncBadgeFooter(summary),
  });
  card.classList.add('col-span-12');
  return card;
}

function homeGrid(bundle: HomeBundle, isOwner: boolean): HTMLElement[] {
  const out: HTMLElement[] = [];

  // Row 1: Latest lab (12)
  out.push(latestLabBlock(bundle, isOwner));

  // Row 2: OURA (4) + Nutrition (4) + WHOOP (4)
  out.push(ouraCardHome(bundle));
  out.push(nutritionCardHome(bundle));
  out.push(whoopCardHome(bundle));

  // Row 3: Measurements (12) — переместили выше препаратов
  out.push(measurementsCardHome(bundle));

  // Row 4: Active meds (12)
  out.push(medsCardHome(bundle, isOwner));

  // Row 5: К обсуждению (12) — последний блок
  out.push(nextCallCard(bundle.next_call, isOwner));

  return out;
}

